/**
 * Shared customer-facing cart-property formatter.
 *
 * Raw Shopify line properties remain unchanged for fulfillment. This module
 * only normalizes, filters, de-duplicates, and orders the properties rendered
 * by snippets/cart-item-properties.liquid on the cart page and cart drawer.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.ZugCartPropertyFormatter = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  var FAMILY_ORDER = {
    apparel: ['Garment Color', 'Size Breakdown', 'Print Locations', 'Turnaround', 'Front Artwork', 'Back Artwork', 'Artwork', 'Order Notes'],
    step_repeat: ['Package', 'Turnaround', 'Artwork', 'Order Notes'],
    retractable: ['Size', 'Configuration', 'Turnaround', 'Artwork', 'Order Notes'],
    yard_sign: ['Size', 'Material', 'Print Sides', 'Stake', 'Turnaround', 'Artwork', 'Order Notes'],
    roll_labels: ['Size', 'Shape', 'Material', 'Finish', 'Pieces', 'Application', 'Unwind', 'Turnaround', 'Artwork', 'Order Notes'],
    label_sheets: ['Sheet Size', 'Shape', 'Material', 'Finish', 'Pieces', 'Turnaround', 'Artwork', 'Order Notes'],
    stickers: ['Size', 'Shape', 'Material', 'Finish', 'Application Side', 'Pieces', 'Turnaround', 'Artwork', 'Order Notes'],
    sticker_sheets: ['Sheet Size', 'Shape', 'Material', 'Finish', 'Pieces', 'Turnaround', 'Artwork', 'Order Notes'],
    buttons: ['Size', 'Pieces', 'Turnaround', 'Artwork', 'Order Notes'],
    banner: ['Size', 'Material', 'Finishing', 'Pole Pockets', 'Wind Slits', 'Turnaround', 'Artwork', 'Order Notes'],
    acrylic: ['Size', 'Shape', 'Material', 'Finish', 'Hardware', 'Attachment', 'Hole Placement', 'Pieces', 'Turnaround', 'Artwork', 'Order Notes'],
    acrylic_sign: ['Size', 'Shape', 'Material', 'Mounting', 'Turnaround', 'Artwork', 'Order Notes']
  };

  var LABEL_ALIASES = {
    'production': 'Turnaround',
    'production time': 'Turnaround',
    'turnaround option': 'Turnaround',
    'rush': 'Turnaround',
    'rush production': 'Turnaround',
    'turnaround': 'Turnaround',
    'artwork method': 'Artwork',
    'uploaded artwork': 'Artwork',
    'artwork status': 'Artwork',
    'artwork': 'Artwork',
    'front artwork': 'Front Artwork',
    'back artwork': 'Back Artwork',
    'print location': 'Print Locations',
    'print locations': 'Print Locations',
    'material or finish': 'Material',
    'application method': 'Application',
    'application': 'Application',
    'unwind direction': 'Unwind',
    'roll unwind orientation': 'Unwind',
    'unwind orientation': 'Unwind',
    'unwind': 'Unwind',
    'quantity': 'Pieces',
    'pieces': 'Pieces',
    'finished size': 'Size',
    'size label': 'Size',
    'order note': 'Order Notes',
    'notes': 'Order Notes'
  };

  function normalizeText(value) {
    return String(value == null ? '' : value)
      .toLowerCase()
      .replace(/&amp;/g, 'and')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  function decodeHtmlEntities(value) {
    var namedEntities = {
      amp: '&',
      apos: "'",
      gt: '>',
      lt: '<',
      nbsp: '\u00a0',
      quot: '"',
      times: '×'
    };
    var decoded = String(value == null ? '' : value);

    for (var pass = 0; pass < 3; pass += 1) {
      var next = decoded.replace(/&(#(?:x[0-9a-f]+|\d+)|[a-z]+);/gi, function (match, entity) {
        if (entity.charAt(0) !== '#') {
          return Object.prototype.hasOwnProperty.call(namedEntities, entity.toLowerCase())
            ? namedEntities[entity.toLowerCase()]
            : match;
        }

        var isHex = entity.charAt(1).toLowerCase() === 'x';
        var codePoint = parseInt(entity.slice(isHex ? 2 : 1), isHex ? 16 : 10);
        if (!Number.isFinite(codePoint) || codePoint <= 0 || codePoint > 0x10ffff || (codePoint >= 0xd800 && codePoint <= 0xdfff)) {
          return match;
        }
        return String.fromCodePoint(codePoint);
      });
      if (next === decoded) break;
      decoded = next;
    }

    return decoded;
  }

  function normalizeDimensionDecimals(value) {
    return String(value == null ? '' : value).replace(/\b(\d+)\.(\d+)(?=\s*(?:"|″))/g, function (match, whole, fraction) {
      var trimmedFraction = fraction.replace(/0+$/, '');
      return trimmedFraction ? whole + '.' + trimmedFraction : whole;
    });
  }

  function normalizeDisplayValue(value) {
    return normalizeDimensionDecimals(decodeHtmlEntities(value)).trim();
  }

  function canonicalLabel(label, family) {
    var normalized = normalizeText(label);
    var canonical = LABEL_ALIASES[normalized] || String(label || '').trim();
    if ((family === 'label_sheets' || family === 'sticker_sheets') && canonical === 'Size') {
      return 'Sheet Size';
    }
    return canonical;
  }

  function normalizeArtworkValue(value) {
    var normalized = normalizeText(value);
    if (!normalized) return '';
    if (normalized.indexOf('upload later') !== -1 || normalized.indexOf('email artwork later') !== -1 || normalized === 'email later') {
      return 'Upload Later';
    }
    if (normalized.indexOf('reorder') !== -1 || normalized.indexOf('previous order') !== -1) {
      return 'Reorder';
    }
    if (normalized.indexOf('uploaded') !== -1 || normalized.indexOf('received') !== -1 || normalized === 'upload artwork') {
      return 'Uploaded';
    }
    return String(value).trim();
  }

  function isInternalProperty(label, value) {
    var rawLabel = String(label || '').trim();
    var normalizedLabel = normalizeText(rawLabel);
    var normalizedValue = normalizeText(value);
    if (!rawLabel || !normalizedValue || rawLabel.charAt(0) === '_') return true;
    if (/^(artwork status|original artwork|proof preview|artwork note|job id)$/.test(normalizedLabel)) return true;
    if (/^(calc line uid|product|product type|product handle|product url|product image)$/.test(normalizedLabel)) return true;
    if (/(pricing|price adjustment|carrier|recipe|controller|family identifier|internal quantity|expected total)/.test(normalizedLabel)) return true;
    if (/(filename|file name|storage url|upload url|uploaded file url|artwork file|upload token)/.test(normalizedLabel)) return true;
    if (/(^| )(uid|identifier|variant id|recipe id|controller id|material key|size key|quantity key)( |$)/.test(normalizedLabel)) return true;
    if (/^(https?:\/\/|www\.)/i.test(String(value).trim())) return true;
    if (/\/uploads\/|\.myshopify\.com|cdn\.shopify\.com\/s\/files/i.test(String(value))) return true;
    if (/(token|identifier| uid| id)$/.test(normalizedLabel) && /^[a-z0-9_-]{16,}$/i.test(String(value).trim())) return true;
    return false;
  }

  function detectFamily(context, entries) {
    context = context || {};
    var explicit = normalizeText(context.family).replace(/ /g, '_');
    if (FAMILY_ORDER[explicit]) return explicit;

    var properties = context.properties || {};
    if (properties._roll_labels === 'true') return 'roll_labels';
    if (properties._label_sheets === 'true') return 'label_sheets';
    if (properties._sticker_sheets === 'true') return 'sticker_sheets';
    if (properties._step_repeat_recipe) return 'step_repeat';
    if (properties._fixed_shape_recipe && normalizeText(properties._fixed_shape_recipe).indexOf('button') !== -1) return 'buttons';
    if (properties._product_family === 'apparel') return 'apparel';
    if (properties._product_family === 'acrylic') {
      return properties._acrylic_subtype === 'sign' ? 'acrylic_sign' : 'acrylic';
    }

    var recipe = normalizeText(properties._banner_recipe || '');
    if (recipe === 'retractable') return 'retractable';
    if (recipe === 'yard sign') return 'yard_sign';
    if (recipe || properties._banner_config_version) return 'banner';

    var handle = normalizeText(context.handle);
    var type = normalizeText(context.productType);
    var productTypeValue = '';
    (entries || []).some(function (entry) {
      if (normalizeText(entry.label) === 'product type') {
        productTypeValue = normalizeText(entry.value);
        return true;
      }
      return false;
    });
    var identity = [handle, type, productTypeValue].join(' ');
    if (/step and repeat|step repeat/.test(identity)) return 'step_repeat';
    if (/retractable/.test(identity)) return 'retractable';
    if (/yard sign/.test(identity)) return 'yard_sign';
    if (/roll label/.test(identity)) return 'roll_labels';
    if (/label sheet/.test(identity)) return 'label_sheets';
    if (/sticker sheet/.test(identity)) return 'sticker_sheets';
    if (/button/.test(identity)) return 'buttons';
    if (/acrylic sign/.test(identity)) return 'acrylic_sign';
    if (/acrylic/.test(identity)) return 'acrylic';
    if (/banner|flag|canopy/.test(identity)) return 'banner';
    if (/shirt|apparel/.test(identity)) return 'apparel';
    if (/sticker/.test(identity)) return 'stickers';
    return 'unknown';
  }

  function sourcePriority(entry, canonical) {
    var source = normalizeText(entry.label);
    if (canonical === 'Turnaround' && source === 'turnaround') return 3;
    if (canonical === 'Artwork' && source === 'artwork') return 3;
    return 1;
  }

  function numericValue(value) {
    var stripped = String(value == null ? '' : value).replace(/,/g, '').trim();
    return /^\d+(?:\.0+)?$/.test(stripped) ? Number(stripped) : NaN;
  }

  function isSampleEntry(entry) {
    var label = normalizeText(entry && entry.label);
    var value = normalizeText(entry && entry.value);
    return (label === 'sample mode' && value === 'true') ||
      (label === 'product type' && value === 'sample') ||
      (label === 'sample offer' && /^(yes|true)$/.test(value));
  }

  function formatProperties(entries, context) {
    context = context || {};
    entries = Array.isArray(entries) ? entries : [];
    var family = detectFamily(context, entries);
    var sampleMode = entries.some(isSampleEntry);
    var titleKey = normalizeText(context.productTitle);
    var visibleQuantity = numericValue(context.displayQuantity != null ? context.displayQuantity : context.lineQuantity);
    var candidates = [];

    entries.forEach(function (entry, index) {
      var label = String(entry && entry.label || '').trim();
      var value = normalizeDisplayValue(entry && entry.value);
      var canonical = canonicalLabel(label, family);
      if (isInternalProperty(canonical, value)) return;

      var normalizedValue = normalizeText(value);
      if (titleKey && normalizedValue === titleKey) return;
      if (canonical === 'Garment' && titleKey && normalizedValue === titleKey) return;

      if (canonical === 'Pieces') {
        var configuredQuantity = numericValue(value);
        if (family === 'apparel' || (Number.isFinite(configuredQuantity) && configuredQuantity === visibleQuantity)) return;
      }

      if ((canonical === 'Pole Pockets' || canonical === 'Wind Slits') && /^(none|no|not selected|n\/a)$/i.test(value)) return;
      if (canonical === 'Artwork' || canonical === 'Front Artwork' || canonical === 'Back Artwork') {
        value = normalizeArtworkValue(value);
        if (!value) return;
      }

      candidates.push({
        label: canonical,
        value: value,
        index: index,
        priority: sourcePriority(entry, canonical)
      });
    });

    if (family === 'roll_labels') {
      var application = candidates.find(function (entry) { return entry.label === 'Application'; });
      var machineApplied = application && normalizeText(application.value).indexOf('machine') !== -1;
      if (!machineApplied) {
        candidates = candidates.filter(function (entry) { return entry.label !== 'Unwind'; });
      }
    }

    var hasPlacementArtwork = candidates.some(function (entry) {
      return entry.label === 'Front Artwork' || entry.label === 'Back Artwork';
    });
    if (hasPlacementArtwork) {
      candidates = candidates.filter(function (entry) { return entry.label !== 'Artwork'; });
    }

    var byLabel = {};
    candidates.forEach(function (entry) {
      var key = normalizeText(entry.label);
      var existing = byLabel[key];
      if (!existing || entry.priority > existing.priority) {
        byLabel[key] = entry;
      } else if (entry.priority === existing.priority && normalizeText(entry.value) !== normalizeText(existing.value)) {
        existing.value += ' · ' + entry.value;
      }
    });
    candidates = Object.keys(byLabel).map(function (key) { return byLabel[key]; });

    var order = FAMILY_ORDER[family];
    if (order && sampleMode) {
      order = ['Sample Product'].concat(order, ['Sample Offer']);
    }
    if (order) {
      candidates = candidates.filter(function (entry) {
        return order.indexOf(entry.label) !== -1;
      });
      candidates.sort(function (a, b) {
        var aIndex = order.indexOf(a.label);
        var bIndex = order.indexOf(b.label);
        return aIndex === bIndex ? a.index - b.index : aIndex - bIndex;
      });
    } else {
      candidates.sort(function (a, b) { return a.index - b.index; });
    }

    candidates.forEach(function (entry) {
      if (entry.label === 'Print Locations' && !/[,&/+]| and /i.test(entry.value)) {
        entry.label = 'Print Location';
      }
    });

    return { family: family, sampleMode: sampleMode, properties: candidates };
  }

  function registerElement(rootWindow) {
    if (!rootWindow || !rootWindow.customElements || rootWindow.customElements.get('zug-cart-property-list')) return;
    var BaseElement = rootWindow.HTMLElement;
    var CartPropertyList = class extends BaseElement {
      connectedCallback() {
        if (!this._sourceEntries) {
          var nodes = Array.prototype.slice.call(this.querySelectorAll('[data-cart-property]'));
          this._sourceEntries = nodes.map(function (node) {
            return { label: node.dataset.label || '', value: node.dataset.value || '' };
          });
        }
        var result = formatProperties(this._sourceEntries, {
          family: this.dataset.family || '',
          productTitle: this.dataset.productTitle || '',
          handle: this.dataset.productHandle || '',
          productType: this.dataset.productType || '',
          lineQuantity: this.dataset.lineQuantity || '',
          displayQuantity: this.dataset.displayQuantity || ''
        });
        var list = this.querySelector('[data-cart-property-items]');
        if (!list) return;
        list.replaceChildren();
        result.properties.forEach(function (property) {
          var item = rootWindow.document.createElement('li');
          item.className = 'zug-cart-properties__item';
          var label = rootWindow.document.createElement('span');
          label.className = 'zug-cart-properties__label';
          label.textContent = property.label + ': ';
          var value = rootWindow.document.createElement('span');
          value.className = 'zug-cart-properties__value';
          value.textContent = property.value;
          item.append(label, value);
          list.appendChild(item);
        });
        this.dataset.family = result.family;
        this.hidden = result.properties.length === 0;
      }
    };
    rootWindow.customElements.define('zug-cart-property-list', CartPropertyList);
  }

  if (typeof window !== 'undefined') registerElement(window);

  return {
    FAMILY_ORDER: FAMILY_ORDER,
    canonicalLabel: canonicalLabel,
    decodeHtmlEntities: decodeHtmlEntities,
    detectFamily: detectFamily,
    formatProperties: formatProperties,
    isInternalProperty: isInternalProperty,
    isSampleEntry: isSampleEntry,
    normalizeArtworkValue: normalizeArtworkValue,
    normalizeDimensionDecimals: normalizeDimensionDecimals,
    normalizeDisplayValue: normalizeDisplayValue,
    normalizeText: normalizeText,
    registerElement: registerElement
  };
});
